import { type Alert, AlertList } from '@deizi/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { readPref, writePref } from '../lib/storage';

const COLORS: Record<Alert['severity'], string> = {
  err: 'var(--err)',
  warn: 'var(--warn)',
  info: 'var(--info)',
};

function readIds(): string[] {
  try {
    const parsed: unknown = JSON.parse(readPref('alerts-read') ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** The alerts bell: polled every minute; "lido" is remembered in this browser only. */
export function useAlerts() {
  const q = useQuery({
    queryKey: ['alerts'],
    queryFn: () => api.get('/alerts', AlertList),
    refetchInterval: 60_000,
  });
  const [read, setRead] = useState<string[]>(readIds);
  const alerts = q.data?.data ?? [];
  const unread = alerts.filter((a) => !read.includes(a.id)).length;
  const markAll = () => {
    // Keep only ids still showing, so the list never grows forever.
    const ids = alerts.map((a) => a.id);
    writePref('alerts-read', JSON.stringify(ids));
    setRead(ids);
  };
  return { alerts, unread, read, markAll, loading: q.isPending };
}

export function AlertsList({
  state,
  onPick,
}: {
  state: ReturnType<typeof useAlerts>;
  onPick: () => void;
}) {
  const { alerts, unread, read, markAll, loading } = state;
  return (
    <>
      <div className="row between" style={{ padding: '8px 10px 10px' }}>
        <h3>Alertas</h3>
        {unread ? (
          <button type="button" className="btn sm g" onClick={markAll}>
            Marcar todos como lidos
          </button>
        ) : null}
      </div>
      {!loading && alerts.length === 0 ? (
        <p className="sm muted" style={{ padding: '4px 10px 14px' }}>
          Nenhum alerta agora. Filamento baixo, prazos de envio, manutenção, parcelas e backups
          aparecem aqui.
        </p>
      ) : null}
      {alerts.map((a) => {
        const isNew = !read.includes(a.id);
        return (
          <Link
            key={a.id}
            to={a.link}
            onClick={onPick}
            style={{
              alignItems: 'flex-start',
              padding: 10,
              gap: 12,
              minHeight: 0,
              background: isNew ? 'var(--sunk)' : undefined,
            }}
          >
            <span className="dot" style={{ background: COLORS[a.severity], marginTop: 6 }} />
            <span className="grow" style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: isNew ? 600 : 500 }}>{a.title}</span>
              <span className="sm muted" style={{ fontWeight: 400 }}>
                {a.detail}
              </span>
            </span>
          </Link>
        );
      })}
    </>
  );
}

export function BellCount({ unread }: { unread: number }) {
  return unread ? (
    <span className="count" style={{ position: 'absolute', top: -6, right: -6 }}>
      {unread}
    </span>
  ) : null;
}
